/* eslint-disable @typescript-eslint/naming-convention */
import { useAppSelector } from 'store/hooks';
import React from 'react';
import { Typography } from '@mui/material';
import { stringify } from 'query-string';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { IPointOfSaleSettingsProps } from 'components/settings/PointOfSaleSettings';
import { SettingDisplay } from 'lib/SettingDisplay';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { OAuthProvider } from '@pepperhq/location-sdk';

export const LightspeedKSeriesSettingsDisplay: React.FC<IPointOfSaleSettingsProps> = ({
    settings,
    locationId,
    onSettingsSaved
}) => {
    const isAuthorized = React.useMemo(() => !!settings?.lightspeedKSeries?.oauth2AccessToken, [settings]);
    const [loadingOAuth, setLoadingOAuth] = React.useState(false);
    const { tenantId } = useAppSelector(state => state.auth.tenant);

    const handleAuthorizeClicked = React.useCallback(async () => {
        setLoadingOAuth(true);
        if (isAuthorized) {
            await new LocationSettingsApi().revokeOAuthAccess(OAuthProvider.LIGHTSPEED_K_SERIES, locationId);
            if (typeof onSettingsSaved === 'function') {
                await onSettingsSaved();
            }
        } else {
            const authHost = import.meta.env.TIER === 'beta' ? 'auth.lsk-demo.app' : 'auth.lsk-prod.app';
            const query = {
                response_type: 'code',
                client_id: import.meta.env.LIGHTSPEED_K_SERIES_CLIENT_ID,
                redirect_uri: `${import.meta.env.MENU_SERVICE_URL}/oauth/lightspeedKSeries`,
                scope: 'orders-api offline_access',
                state: JSON.stringify({
                    tenantId,
                    locationId,
                    successRedirectUrl: `${window.location.href}?oauthResult=success`,
                    errorRedirectUrl: `${window.location.href}?oauthResult=error`
                })
            };
            window.location.href = `https://${authHost}/realms/k-series/protocol/openid-connect/auth?${stringify(
                query
            )}`;
        }
        setLoadingOAuth(false);
    }, [isAuthorized, locationId, tenantId, onSettingsSaved]);

    if (locationId === undefined) {
        return null;
    }
    return (
        <>
            <SettingDisplay label="Business Location ID">
                <Typography variant="body1">{settings?.lightspeedKSeries?.locationId}</Typography>
            </SettingDisplay>
            <SettingDisplay label="OAuth2">
                <LoadingButton
                    variant={isAuthorized ? 'outlined' : 'contained'}
                    color="primary"
                    onClick={handleAuthorizeClicked}
                    loading={loadingOAuth}
                >
                    {isAuthorized ? 'De-Authorize' : 'Authorize'}
                </LoadingButton>
            </SettingDisplay>
        </>
    );
};
